import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { titleize } from 'underscore.string';
import { TriangleAlert } from 'lucide-react';
import MissingEquipmentWarning from 'components/modules/orders/order-rtb/MissingEquipmentWarning';
import {
  Tooltip as RadixTooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
} from '@cargomatic/ui';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { sluggify } from 'utils/localization/utils';
import { getInvoicesForShipment } from 'utils/AccountingWebAPIUtils';
import { withTranslation, Translation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { getShipmentTimezone } from 'helpers/shipments';
import CargomaticAlert from 'components/ui/Alert';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import {
  checkProcessedAccessorial,
  checkIfAccessorialsNeedsReview,
  checkIfEquipmentIsMissing,
  checkIfEquipmentIsMissingForAccessorial,
  checkIfAccessorialAutoDeclined,
} from 'helpers/accessorials';

const TOOLTIP_TEXTS = {
  SHIPPER_CHANGED: (
    <Translation>{(t) => t('SHIPPER_CHANGED_NOTE')}</Translation>
  ),
};
class AccessorialRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      accessorials: props.accessorials || [],
      accessorialRequestIds: props.accessorialRequestIds || [],
      selectedAccessorials: [],
      invoices: [],
      autoDeclineDates: {},
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.accessorials !== this.state.accessorials) {
      this.getInvoices();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      selectedAccessorials: nextProps.selectedAccessorials,
      accessorials: nextProps.accessorials,
      accessorialRequestIds: nextProps.accessorialRequestIds,
    });
  }

  componentWillUnmount() {}

  getInvoices = async () => {
    const shipmentId = this.props.shipment._id;
    const {
      entity: { invoices },
    } = await getInvoicesForShipment(shipmentId);
    this.setState((prev) => {
      return {
        ...prev,
        invoices,
      };
    });
  };

  getElapsedTime = (time) => {
    const now = moment();
    const timeToDiff = moment(time);
    const days = now.diff(timeToDiff, 'days');
    const hours = now.diff(timeToDiff, 'hours');
    const minutes = now.diff(timeToDiff, 'minutes');
    const seconds = now.diff(timeToDiff, 'seconds');

    if (days > 0) {
      return `${days}d`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  getStatus = (accessorial) => {
    const { carrier_status, shipper_status } = accessorial;
    const statusesDiffer =
      carrier_status && shipper_status && carrier_status !== shipper_status;

    if (statusesDiffer) {
      return {
        carrier_status,
        shipper_status,
      };
    }
    return { status: shipper_status };
  };

  calculateCost = (rate, qty = 1) => {
    if (!rate) {
      return 0;
    }
    return parseFloat(rate) * parseFloat(qty);
  };

  accessorialIsSelected = (accessorial) =>
    this.state.selectedAccessorials.includes(accessorial._id);

  allAccessorialsSelected = () => {
    const { accessorials, selectedAccessorials } = this.state;
    return (
      accessorials.length === selectedAccessorials.length &&
      selectedAccessorials.length
    );
  };

  selectAll = (e) => {
    const { accessorials } = this.state;
    const selectedAccessorials = e.target.checked ? accessorials : [];
    const unprocessedAccessorials = selectedAccessorials.filter(
      (accessorial) =>
        !checkProcessedAccessorial(accessorial) &&
        !checkIfAccessorialAutoDeclined(accessorial),
    );
    const selectedIds = unprocessedAccessorials.map((acc) => acc._id);
    this.setState({ selectedAccessorials: selectedIds }, () =>
      this.props.onAccessorialSelect(unprocessedAccessorials),
    );
  };

  selectAccessorial = (e, id) => {
    const checked = e.target.checked;
    this.setState((currentState) => {
      const { selectedAccessorials: currentAccessorials } = currentState;
      const selectedAccessorials = checked
        ? currentAccessorials.concat(id)
        : currentAccessorials.filter((acc) => acc !== id);

      const fullSelectedAccessorials = this.state.accessorials.filter((acc) =>
        selectedAccessorials.includes(acc._id),
      );
      this.props.onAccessorialSelect(fullSelectedAccessorials);
      return {
        selectedAccessorials,
      };
    });
  };

  renderMissingDocumentsLabel = () => {
    const { accessorials } = this.state;

    // Don't show the missing document label if ALL accessorials are declined
    const allAccessorialsDeclined = accessorials.every((accessorial) => {
      const { status } = this.getStatus(accessorial);
      return status === 'declined';
    });
    if (allAccessorialsDeclined) return;

    if (accessorials && accessorials.length) {
      const { t } = this.props;
      const requestsAreMissingDocuments = accessorials.some((acc) => {
        const isAccessorialNotDeclined =
          this.getStatus(acc)?.status !== 'declined';
        return isAccessorialNotDeclined && acc.missing_docs;
      });
      if (requestsAreMissingDocuments) {
        return (
          <CargomaticAlert
            dataTestId="CargomaticAlert-5c604c0d"
            className="my-4"
            type="error"
          >
            {t('ACCESSORIALS_WITH_MISSING_DOCUMENTS')}
          </CargomaticAlert>
        );
      }
    }
  };

  renderHeader = () => {
    const { t } = this.props;
    return (
      <TableRow data-testid="tr-53e40358">
        <TableHead data-testid="th-ed2f9396">
          {this.renderSelectAll()}
        </TableHead>
        <TableHead data-testid="th-bb6ee6b9" className="">
          {t('ADJUSTMENT')}
        </TableHead>
        <TableHead data-testid="th-1b53eb03" className="text-right">
          {t('SHIPPER_COST')}
        </TableHead>
        <TableHead data-testid="th-1b22eb22">{t('SHIPPER_SOURCE')}</TableHead>
        <TableHead data-testid="th-0d2e9168" className="text-right">
          {t('CARRIER_OR_VENDOR_COST')}
        </TableHead>
        <TableHead data-testid="th-2253eb2">{t('CARRIER_SOURCE')}</TableHead>
        <TableHead data-testid="th-b8729c3f">
          {titleize(t('NAME', { count: 1 }))}
        </TableHead>
        <TableHead data-testid="th-1b9b0b78">
          {titleize(t('SHIPPER', { count: 1 }))} {titleize(t('STATUS'))}
        </TableHead>
        <TableHead data-testid="th-1b9b0b79">
          {titleize(t('CARRIER', { count: 1 }))} {titleize(t('STATUS'))}
        </TableHead>
        <TableHead data-testid="th-b8729c3f">{t('CREATED_BY')}</TableHead>
        <TableHead data-testid="th-f64267fb">
          {t('MISSING_DOCUMENTS')}?
        </TableHead>
        <TableHead data-testid="th-3e42809c">{t('ACTION_REQUIRED')}</TableHead>
        <TableHead data-testid="th-3028f0d1">{t('UPDATED')}</TableHead>
      </TableRow>
    );
  };

  renderFooter = () => {
    const { t } = this.props;
    let shipperTotal = 0;
    let carrierTotal = 0;

    this.state.accessorials.forEach((accessorial) => {
      const { carrier_rate, shipper_rate, carrier_unit_qty, shipper_unit_qty } =
        accessorial;
      shipperTotal += this.calculateCost(shipper_rate || 0, shipper_unit_qty);
      carrierTotal += this.calculateCost(carrier_rate || 0, carrier_unit_qty);
    });
    return (
      <TableRow data-testid="tr-0c507a61">
        <TableCell data-testid="td-bec09f44" />
        <TableCell data-testid="td-73ce3420" className="uppercase">
          {t('TOTAL')}
        </TableCell>
        <TableCell
          data-testid="td-95882bf1"
          className="uppercase font-bold text-right"
        >
          {formatMoney(shipperTotal, this.state.accessorials[0]?.currency)}
        </TableCell>
        <TableCell data-testid="td-73220990">&nbsp;</TableCell>
        <TableCell
          data-testid="td-05c0b30d"
          className="uppercase font-bold text-right"
        >
          {formatMoney(carrierTotal, this.state.accessorials[0]?.currency)}
        </TableCell>
        <TableCell colSpan={8}>&nbsp;</TableCell>
      </TableRow>
    );
  };

  renderBody = () => {
    return this.state.accessorials.map((accessorial) =>
      this.renderRow(accessorial),
    );
  };

  renderStatusColumns = (accessorial) => {
    let { shipper_status, carrier_status, status } =
      this.getStatus(accessorial);

    if (!status) {
      carrier_status =
        (carrier_status || '').indexOf('pending') > -1
          ? 'pending'
          : carrier_status;
      shipper_status =
        (shipper_status || '').indexOf('pending') > -1
          ? 'pending'
          : shipper_status;

      return {
        shipperCell: <AccessorialRequestStatusBadge status={shipper_status} />,
        carrierCell: <AccessorialRequestStatusBadge status={carrier_status} />,
      };
    }
    status = (status || '').indexOf('pending') > -1 ? 'pending' : status;
    const badge = <AccessorialRequestStatusBadge status={status} />;
    return { shipperCell: badge, carrierCell: badge };
  };

  renderMissingDocumentsColumn = ({ shipperAutoDeclineDate }) => {
    if (!(shipperAutoDeclineDate && this.props.shipment)) {
      return null;
    }
    const timezone = getShipmentTimezone(this.props.shipment);
    return (
      <div
        data-testid="div-bf4d3ea9"
        className="flex text-red-700 justify-between"
      >
        <TriangleAlert className="w-6 h-6 m-auto" />
        <p data-testid="p-2ba8bb00" className="pl-1">{`Due on ${moment
          .tz(shipperAutoDeclineDate, timezone)
          .format('L')}`}</p>
      </div>
    );
  };

  renderRow = (accessorial) => {
    const { t } = this.props;
    const { shipper_status, status } = this.getStatus(accessorial);

    let { action_required: actionRequired } = accessorial;
    const isMissingEquipment =
      checkIfEquipmentIsMissingForAccessorial(accessorial);

    if (['declined', 'approved', 'accepted'].includes(actionRequired)) {
      actionRequired = '';
    }
    const shouldRenderMissingDocumentsDueDate =
      featureEnabled(CONFIGURED_FEATURES.SSR) && status !== 'declined';
    return (
      <TableRow data-testid="tr-b6ab6c53" className="">
        <TableCell data-testid="td-09bcabc0">
          {this.renderSelect(accessorial)}
        </TableCell>
        <TableCell data-testid="td-33651e18" className="">
          <div className="flex gap-2">
            <a
              data-testid="a-90d45db8"
              onClick={() => this.props.handleRequestSelect(accessorial)}
            >
              {accessorial.message}
            </a>
            {isMissingEquipment && <MissingEquipmentWarning />}
          </div>
        </TableCell>
        <TableCell data-testid="td-cb5b9c03" className="text-right">
          {(accessorial.shipper_rate < 0 || accessorial.shipper_rate > 0) &&
            `${formatMoney(
              this.calculateCost(
                accessorial.shipper_rate,
                accessorial.shipper_unit_qty,
              ),
              accessorial?.currency,
            )}`}
        </TableCell>
        <TableCell data-testid="td-ab220990">
          {t(sluggify(accessorial?.receivable_rate_source || ''))}
        </TableCell>
        <TableCell data-testid="td-3540c6ee" className="text-right">
          {(accessorial.carrier_rate < 0 || accessorial.carrier_rate > 0) &&
            `${formatMoney(
              this.calculateCost(
                accessorial.carrier_rate,
                accessorial.carrier_unit_qty,
              ),
              accessorial?.currency,
            )}`}
        </TableCell>
        <TableCell data-testid="td-cb09c22">
          {t(sluggify(accessorial?.payable_rate_source || ''))}
        </TableCell>
        <TableCell data-testid="td-b6043b90" className="line-clamp-3">
          {accessorial.payee_type === 'carrier'
            ? unescapeSpecialChars(accessorial.carrier_name)
            : unescapeSpecialChars(accessorial.vendor_name)}
        </TableCell>
        <TableCell data-testid="td-923358b7">
          {this.renderStatusColumns(accessorial).shipperCell}
        </TableCell>
        <TableCell data-testid="td-923358b8">
          {this.renderStatusColumns(accessorial).carrierCell}
        </TableCell>
        <TableCell data-testid="td-911358b7">
          {accessorial?.created_by}
        </TableCell>
        <TableCell data-testid="td-ad8d5a18" style={{ color: '#ddd' }}>
          {shouldRenderMissingDocumentsDueDate &&
            this.renderMissingDocumentsColumn(accessorial)}
        </TableCell>
        <TableCell
          data-testid="td-185df130"
          style={{
            color:
              (status || shipper_status) === 'pending-admin'
                ? '#00a9db'
                : '#ddd',
          }}
        >
          <a
            data-testid="a-882ae2ca"
            onClick={() => this.props.handleRequestSelect(accessorial)}
          >
            {t(sluggify(actionRequired))}
          </a>
        </TableCell>
        <TableCell data-testid="td-d1a9183a" style={{ color: '#00a9db' }}>
          <a
            data-testid="a-6a39eb6e"
            onClick={() => this.props.handleRequestSelect(accessorial)}
          >
            {this.getElapsedTime(
              accessorial.modified_at || accessorial.created_at,
            )}
          </a>
        </TableCell>
      </TableRow>
    );
  };

  renderSelect(accessorial) {
    const accessorialDeclinedBothSide =
      checkIfAccessorialAutoDeclined(accessorial);
    const disabled =
      checkProcessedAccessorial(accessorial) || accessorialDeclinedBothSide;
    if (accessorialDeclinedBothSide) {
      return (
        <TooltipProvider>
          <RadixTooltip>
            <TooltipTrigger asChild>
              <input
                data-testid="input-4debbc29"
                className="checkbox-center"
                type="checkbox"
                checked={this.accessorialIsSelected(accessorial)}
                onChange={(e) => this.selectAccessorial(e, accessorial._id)}
                disabled={disabled}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              {TOOLTIP_TEXTS.SHIPPER_CHANGED}
            </TooltipContent>
          </RadixTooltip>
        </TooltipProvider>
      );
    }
    return (
      <input
        data-testid="input-4debbc29"
        className="checkbox-center"
        type="checkbox"
        checked={this.accessorialIsSelected(accessorial)}
        onChange={(e) => this.selectAccessorial(e, accessorial._id)}
        disabled={disabled}
      />
    );
  }

  renderSelectAll() {
    return (
      <input
        data-testid="input-680e7fe6"
        className="checkbox-center"
        type="checkbox"
        checked={this.allAccessorialsSelected()}
        onChange={this.selectAll}
      />
    );
  }

  render() {
    const { dataTestId, t } = this.props;
    const { selectedAccessorials = [], accessorials = [] } = this.state;
    const selectAccessorialsForAutoAppliedNeedReview = accessorials.filter(
      (accessorial) => selectedAccessorials.includes(accessorial._id),
    );
    return (
      <div data-testid={dataTestId} className="overflow-x-auto">
        {featureEnabled(CONFIGURED_FEATURES.SSR) &&
          this.renderMissingDocumentsLabel()}
        {checkIfAccessorialsNeedsReview(
          selectAccessorialsForAutoAppliedNeedReview,
        ) && (
          <CargomaticAlert
            dataTestId="Alert-Auto-Apply-Bulk-Accessorial"
            className="my-4"
            type="warning"
          >
            {t('AUTO_APPLY_BULK_ACCESSORIAL_WARNING_MESSAGE')}
          </CargomaticAlert>
        )}
        {checkIfEquipmentIsMissing(
          selectAccessorialsForAutoAppliedNeedReview,
        ) && (
          <CargomaticAlert
            dataTestId="Alert-Equipment-Bulk-Accessorial"
            className="my-4"
            type="error"
          >
            {t('NOT_APPLIED_TO_AN_EQUIPMENT')}
          </CargomaticAlert>
        )}
        <Table data-testid="table-87053597">
          <TableHeader data-testid="thead-221b5d84">
            {this.renderHeader()}
          </TableHeader>
          <TableBody data-testid="tbody-923a97c6">
            {this.renderBody()}
          </TableBody>
          <TableFooter data-testid="tfoot-cad95bc9">
            {this.renderFooter()}
          </TableFooter>
        </Table>
      </div>
    );
  }
}

AccessorialRequests.propTypes = {
  shipment: PropTypes.object.isRequired,
  handleRequestSelect: PropTypes.func,
  onAccessorialSelect: PropTypes.func.isRequired,
};

export default withTranslation()(AccessorialRequests);
